"use client";

import React, { useState, useEffect, useMemo } from "react";
import { Check, Copy, RefreshCw, Sparkles } from "lucide-react";
import { useRouter } from "next/navigation";
import AlternatePaymentOptions from "./AlternatePaymentOptions";
import { PAYMENT_METHOD } from "@/lib/flutterwave_v4/payment_config";
import { useFlutterwavePaymentPoll } from "@/hooks/useFlutterwavePaymentPoll";
import { useLanguage } from "@/contexts/LanguageContext";
import { getStrings } from "@/i18n/dictionaries";

interface BankTransferDetailsProps {
  bankName: string;
  accountNumber: string;
  amountNgn: number;
  txRef: string;
  expiresAt?: string | null;
  onComplete: () => void;
  onCancel: () => void;
  currency?: string; // Currency code (e.g., "NGN", "GHS", "KES")
  countryCode?: string; // ISO country code for AlternatePaymentOptions
  onSwitchToStripe?: () => void; // Callback to switch to Stripe Card (primary alternative, works with any phone)
  onSwitchToOpay?: () => void; // Callback to switch to OPay payment (Nigeria-specific, requires +234 phone)
  onSwitchToMomo?: () => void; // Callback to switch to Mobile Money
  blacklistError?: { message: string; supportEmail: string } | null; // Blacklist error details
  userEmail?: string; // User's email for support mailto
  userId?: string; // User's ID for support mailto
}

export default function BankTransferDetails({
  bankName,
  accountNumber,
  amountNgn,
  txRef,
  expiresAt,
  onComplete,
  onCancel,
  currency = "NGN", // Default to NGN for backward compatibility
  countryCode,
  onSwitchToStripe,
  onSwitchToOpay,
  onSwitchToMomo,
  blacklistError = null,
  userEmail = "",
  userId = "",
}: BankTransferDetailsProps) {
  const router = useRouter();
  const { locale } = useLanguage();
  const t = useMemo(() => getStrings(locale, "payment"), [locale]);
  const [copiedItem, setCopiedItem] = useState<string | null>(null);

  // ── Shared polling hook ───────────────────────────────────
  const poll = useFlutterwavePaymentPoll({
    txRef,
    onConfirmed: () => {
      onComplete();
      setTimeout(() => router.push("/dashboard"), 1000);
    },
  });
  const isPolling = poll.isChecking;
  const timeoutReached = poll.outcome === "timeout";
  const underpaidDetail = poll.outcome === "underpaid" ? poll.detail : null;

  // Validate and parse amount - log warnings if invalid
  const safeAmount = React.useMemo(() => {
    const num = typeof amountNgn === "number" 
      ? amountNgn 
      : typeof amountNgn === "string" 
        ? Number(amountNgn) 
        : NaN;
    
    if (!Number.isFinite(num) || num <= 0) {
      console.warn(
        "[BankTransferDetails] Invalid amount received:",
        { amountNgn, type: typeof amountNgn }
      );
      return null;
    }
    
    return num;
  }, [amountNgn]);

  // Get currency symbol
  const currencySymbol = React.useMemo(() => {
    const symbols: Record<string, string> = {
      NGN: "₦",
      GHS: "GH₵",
      KES: "KSh",
      ZAR: "R",
      UGX: "USh",
      TZS: "TSh",
      RWF: "FRw",
      ZMW: "ZK",
      XOF: "CFA",
      XAF: "FCFA",
      ETB: "Br",
      MWK: "MK",
      SLL: "Le",
    };
    return symbols[currency.toUpperCase()] || currency.toUpperCase();
  }, [currency]);

  // Sanitize error message for mailto link to prevent XSS and email injection
  // Note: The @ symbol is intentionally excluded to prevent email injection attacks.
  // The full error message (with @) is displayed in the modal UI for user visibility.
  // Only the sanitized version is used in the mailto body for security.
  const sanitizedErrorMessage = React.useMemo(() => {
    if (!blacklistError?.message) return "";
    // Use whitelist approach: only allow alphanumeric, spaces, and basic punctuation
    // Deliberately excluding @ to prevent email injection attacks
    return blacklistError.message
      .replace(/[^a-zA-Z0-9\s.,!?\-()]/g, "") // Whitelist safe characters (no @)
      .substring(0, 500); // Limit length
  }, [blacklistError?.message]);

  const handleCopy = (text: string, item: string) => {
    navigator.clipboard.writeText(text);
    setCopiedItem(item);
    setTimeout(() => setCopiedItem(null), 2000);
  };

  const handleIHavePaid = () => poll.start();

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 p-4">
      <div className="relative w-full max-w-4xl max-h-[calc(100dvh-2rem)] overflow-y-auto rounded-xl bg-white shadow-2xl">
        {/* Header */}
        <div className="border-b border-slate-200 px-4 sm:px-6 py-4">
          <div className="mb-4 flex items-center justify-center">
            <div className="rounded-full bg-emerald-50 p-3">
              <Sparkles className="h-8 w-8 text-emerald-600" />
            </div>
          </div>
          <h2 className="text-center text-2xl font-bold text-slate-900">
            {blacklistError ? t.flw_bank_unavailable : t.complete_payment}
          </h2>
          <p className="mt-2 text-center text-sm text-slate-600">
            {blacklistError 
              ? blacklistError.message
              : t.transfer_exact_desc}
          </p>
        </div>

        {/* Blacklist Error Alert */}
        {blacklistError && (
          <div className="px-4 sm:px-6 py-6">
            <div className="rounded-lg border-2 border-red-200 bg-red-50 p-6">
              <div className="mb-4 flex items-center justify-center">
                <div className="rounded-full bg-red-100 p-3">
                  <svg
                    className="h-8 w-8 text-red-600"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
                    />
                  </svg>
                </div>
              </div>

              <h3 className="mb-3 text-center text-lg font-bold text-red-900">
                {t.flw_bank_unavailable}
              </h3>
              
              <p className="mb-6 text-center text-sm text-red-800">
                {blacklistError.message}
              </p>

              <div className="space-y-3">
                {/* Email Support Button */}
                <a
                  href={`mailto:${blacklistError.supportEmail}?subject=${encodeURIComponent(t.flw_issue_subject)}&body=${encodeURIComponent(`${t.flw_issue_body}\n\nMy email: ${userEmail}\nUser ID: ${userId}\n\nIssue: ${sanitizedErrorMessage}`)}`}
                  className="block w-full rounded-md bg-red-600 px-4 py-3 text-center text-sm font-semibold text-white shadow-sm hover:bg-red-500 transition"
                >
                  {t.email_support.replace("{email}", blacklistError.supportEmail)}
                </a>

                {/* Pay with Stripe Button */}
                {onSwitchToStripe && (
                  <button
                    onClick={() => {
                      onCancel();
                      onSwitchToStripe();
                    }}
                    className="w-full rounded-md bg-indigo-600 px-4 py-3 text-sm font-semibold text-white shadow-sm hover:bg-indigo-500 transition"
                  >
                    {t.pay_with_card}
                  </button>
                )}

                {/* Cancel Button */}
                <button
                  onClick={onCancel}
                  className="w-full rounded-md border border-slate-300 bg-white px-4 py-3 text-sm font-semibold text-slate-700 shadow-sm hover:bg-slate-50 transition"
                >
                  {t.close}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Normal Bank Transfer Content - Only show if NOT blacklisted */}
        {!blacklistError && (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 px-4 sm:px-6 py-6">
          {/* LEFT COLUMN: Bank Transfer Details */}
          <div className="space-y-4">
            {/* Bank Name */}
            <div className="rounded-lg border border-slate-200 bg-slate-50 p-4">
              <p className="text-xs font-medium text-slate-500 uppercase">{t.bank_name}</p>
              <p className="mt-1 text-lg font-semibold text-slate-900">{bankName}</p>
            </div>

            {/* Account Number */}
            <div className="rounded-lg border border-slate-200 bg-slate-50 p-4">
              <p className="text-xs font-medium text-slate-500 uppercase">{t.account_number}</p>
              <div className="mt-1 flex items-center justify-between">
                <p className="text-lg font-semibold text-slate-900">{accountNumber}</p>
                <button
                  onClick={() => handleCopy(accountNumber, 'account')}
                  className="rounded-md p-2 text-slate-600 hover:bg-slate-200 transition"
                  title="Copy account number"
                >
                  {copiedItem === 'account' ? <Check className="h-4 w-4 text-emerald-600" /> : <Copy className="h-4 w-4" />}
                </button>
              </div>
            </div>

            {/* Amount */}
            <div className="rounded-lg border-2 border-emerald-200 bg-emerald-50 p-4">
              <p className="text-xs font-medium text-emerald-700 uppercase">{t.amount_to_transfer}</p>
              <div className="mt-1 flex items-center justify-between">
                <p className="text-2xl font-bold text-emerald-900">
                  {safeAmount !== null 
                    ? `${currencySymbol}${safeAmount.toLocaleString()}` 
                    : `${currencySymbol}—`}
                </p>
                <button
                  onClick={() => {
                    const safeValue = safeAmount !== null ? String(safeAmount) : "0";
                    handleCopy(safeValue, 'amount');
                  }}
                  className="rounded-md p-2 text-emerald-700 hover:bg-emerald-100 transition"
                  title="Copy amount"
                >
                  {copiedItem === 'amount' ? <Check className="h-4 w-4" /> : <Copy className="h-4 w-4" />}
                </button>
              </div>
            </div>
            
            {/* Trust Note */}
            <div className="rounded-lg bg-slate-50 border border-slate-200 p-3">
              <p className="text-xs text-slate-600 text-center">
                <span className="font-medium">{t.secure_payment_note}</span> {t.secure_payment_note_desc}
              </p>
            </div>

            {/* Copy Success Toast */}
            {copiedItem && (
              <div className="rounded-lg bg-emerald-50 border border-emerald-200 p-3">
                <p className="text-xs text-emerald-800 text-center flex items-center justify-center gap-1">
                  <Check className="h-3 w-3" />
                  {copiedItem === 'account' ? t.copied_account : t.copied_amount}
                </p>
              </div>
            )}

            {/* Expiry */}
            {expiresAt && (
              <div className="rounded-lg bg-amber-50 border border-amber-200 p-3">
                <p className="text-xs text-amber-800">
                  <strong>{t.note_label}</strong> {t.account_expires.replace("{date}", (() => {
                    const date = expiresAt ? new Date(expiresAt) : null;
                    return date && !isNaN(date.getTime()) ? date.toLocaleString() : "—";
                  })())}
                </p>
              </div>
            )}

            {/* Instructions */}
            <div className="rounded-lg border border-blue-200 bg-blue-50 p-4">
              <p className="text-xs font-semibold text-blue-900 mb-2">{t.important_instructions}</p>
              <ul className="space-y-1 text-xs text-blue-800">
                <li>• {t.transfer_exact_amount}</li>
                <li>• {t.confirm_5_10_min}</li>
                <li>• {t.click_i_have_paid}</li>
                <li>• {t.keep_page_open}</li>
              </ul>
            </div>
          </div>

          {/* RIGHT COLUMN: Alternative Payment Options */}
          <div className="space-y-4">
            <AlternatePaymentOptions
              countryCode={countryCode || "NG"}
              currentMethod={PAYMENT_METHOD.BANK_TRANSFER}
              onSwitchToStripe={onSwitchToStripe ? () => { onCancel(); onSwitchToStripe(); } : undefined}
              onSwitchToOpay={onSwitchToOpay ? () => { onCancel(); onSwitchToOpay(); } : undefined}
              onSwitchToMomo={onSwitchToMomo ? () => { onCancel(); onSwitchToMomo(); } : undefined}
              disabled={isPolling}
            />

            {/* Help text */}
            <div className="rounded-lg bg-slate-50 border border-slate-200 p-3">
              <p className="text-xs text-slate-600 text-center">
                {t.secure_flutterwave}
              </p>
            </div>
          </div>
        </div>
        )}
        {/* End of Normal Bank Transfer Content */}

        {/* Action Buttons - Only show if NOT blacklisted */}
        {!blacklistError && (
        <div className="border-t border-slate-200 px-4 sm:px-6 py-4">
          <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-3">
            <button
              onClick={handleIHavePaid}
              disabled={isPolling}
              className="flex-1 rounded-md bg-emerald-600 px-4 py-3 text-sm font-semibold text-white shadow-sm hover:bg-emerald-500 transition disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
            >
              {isPolling ? (
                <>
                  <RefreshCw className="h-4 w-4 animate-spin" />
                  {t.checking_payment}
                </>
              ) : (
                t.i_have_paid
              )}
            </button>

            <button
              onClick={onCancel}
              disabled={isPolling}
              className="sm:flex-initial rounded-md border border-slate-300 bg-white px-4 py-3 text-sm font-semibold text-slate-700 shadow-sm hover:bg-slate-50 transition disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {t.cancel}
            </button>
          </div>

          {isPolling && (
            <div className="mt-4 rounded-lg bg-blue-50 border border-blue-200 p-3">
              <p className="text-xs text-blue-800 text-center">
                <RefreshCw className="inline h-3 w-3 animate-spin mr-1" />
                {t.auto_checking}
              </p>
            </div>
          )}

          {underpaidDetail && (
            <div className="mt-4 rounded-lg bg-red-50 border border-red-200 p-3">
              <p className="text-xs text-red-800 text-center font-semibold">
                {underpaidDetail}
              </p>
            </div>
          )}
          
          {timeoutReached && (
            <div className="mt-4 rounded-lg bg-amber-50 border border-amber-200 p-3">
              <p className="text-xs text-amber-800 text-center font-semibold">
                {t.timeout_message}
              </p>
            </div>
          )}
        </div>
        )}
        {/* End of Action Buttons */}
      </div>
    </div>
  );
}
